import { isFunction, isHTMLElement, isString, isSvgTag, isUndefined } from '@/utils/is'
import { isVNode, type Component, type HTMLAttributes, render, isRef } from 'vue'
import type { BaseHTMLAttributes, Ref, VNode } from 'vue'
import { VEC2, Vector } from '../math/vector'
import type { CSSProperties } from 'vue'
import { cssPropsToString } from '../style'
import type { IMouseTooltipProps } from 'components/mouse-tooltip/types'
import { showTooltip } from 'components/mouse-tooltip'

type Renderable = Element | HTMLElement | VNode | Component | string
type RenderableOrFunction = Renderable | ((...args: any[]) => Renderable)
export type TRenderableElement = RenderableOrFunction | RenderableOrFunction[]

export type ElProps = {
  oldEl?: HTMLElement
  css?: CSSProperties
  tooltip?: IMouseTooltipProps
  children?: TRenderableElement[]
  render?: (props: ElProps) => TRenderableElement
  parent?: HTMLElement
  root?: HTMLElement
  reactive?: boolean
}

const getElProps = (el: any): ElProps => {
  if (!el || !el.getProps) return {} as ElProps
  return el.getProps() as ElProps
}

export const parseHTML = <T extends HTMLElement | SVGElement = HTMLElement>(content: string): T => {
  const root = document.createElement('div')
  root.innerHTML = content

  if (root.children.length === 1) {
    return Array.from(root.children)[0] as T
  }

  return root as unknown as T
}

export const parseHTMLToString = (htmlContent: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const textContent = doc.body.textContent || '';
  return textContent.trim().replace(/([.,:;!?])((?!\s|$))/g, '$1 $2');
}

const isTruthy = (v?: any): boolean => {
  if (!v) return false
  if (isString(v) && ['none', 'null', 'undefined', '0', 'false'].includes(v.toLowerCase()))
    return false
  return !!v
}

export const elementWithAttribute = (el: HTMLElement, key: string): HTMLElement | null => {
  if (!isFunction(el.getAttribute)) return el;
  if (isTruthy(el.getAttribute(key))) return el

  const children = Array.from(el.children)
  if (children.length <= 0) return null

  for (const child of children) {
    const w = elementWithAttribute(child as HTMLElement, key)
    if (w) return w
  }

  return null
}

export const elementUpdateAttribute = (el: HTMLElement, key: string, value: any) => {
  if (!isFunction(el.setAttribute)) return;
  const w = elementWithAttribute(el, key)
  if (!w) return
  w.setAttribute(key, value)
}
const svgNS = 'http://www.w3.org/2000/svg'

const makeEl = <
  T extends HTMLElement | SVGElement | SVGLineElement | SVGPathElement = HTMLDivElement
>(
  name: string,
  props: Partial<(BaseHTMLAttributes & HTMLAttributes & ElProps) | { [key: string]: any }> = {}
): T => {
  const isNS = isSvgTag(name)
  //const rel = props.render ? props.render() : undefined;
  const el =
    props.oldEl ||
    ((isNS
      ? document.createElementNS(svgNS, name)
      : document.createElement(name)) as unknown as HTMLElement)

  ;(el as any).getProps = () => props

  const construct = (el: HTMLElement) => {
    el.innerHTML = ''

    const kvs = Object.entries(props)

    for (const [key, value] of kvs) {
      if (['construct', 'tooltip', 'parent', 'oldEl', 'root'].includes(key)) {
        continue
      }
      if (key === 'css') {
        el.setAttribute('style', cssPropsToString(value))
        continue
      }
      if (key === 'children') {
        const children = value as TRenderableElement[]
        for (const child of children) {
          const pz = (child as any).getProps ? (child as any).getProps() : {}
          const childEl = renderElementToHTMLElement(child, {
            ...pz,
            root: props.parent || el,
            parent: el
          })

          if (Array.isArray(childEl)) {
            el.append(...childEl)
          } else {
            el.appendChild(childEl)
          }
        }
        continue
      }
      if (key.startsWith('on') && isFunction(value)) {
        try {
          el.addEventListener(key, value as EventListenerOrEventListenerObject)
        } catch (e) {
          console.warn(e)
        }
        continue
      }

      //console.log(el);
      el.setAttribute(key, isString(value) ? value : value.toString ? value.toString() : value)
      try {
        ;(el as any)[key] = value
      } catch (e) {
        console.error(e)
      }
    }

    if (props.tooltip) {
      showTooltip(el, props.tooltip)
    }

    const e = el as any
    e.construct = construct
  }

  construct(el)

  return el as T
}

export const EL = <
  T extends HTMLElement | SVGElement | SVGLineElement | SVGPathElement = HTMLDivElement
>(
  name: string,
  props: Partial<(BaseHTMLAttributes & HTMLAttributes & ElProps) | { [key: string]: any }> = {}
): T => {
  const el = makeEl<T>(name, props)
  const x = el as any
  const elRender = (propz: ElProps) => {
    const nextProps = { ...props, ...propz, oldEl: el }
    const nextEl = makeEl<T>(name, nextProps)
    return nextEl
  }
  x.flush = elRender
  return el
}

export const getElementSize = (el: HTMLElement): Vector => {
  const rect = el.getBoundingClientRect()

  if (rect.width > 0 && rect.height > 0) return VEC2(rect.width, rect.height)

  const parent = el.parentElement as HTMLElement
  if (!parent) return VEC2(rect.width, rect.height)

  return getElementSize(parent)
}

export const getTextMetrics = (text: string, font: string): TextMetrics | null => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return null;
  context.font = font
  return context.measureText(text)
}

export const getTextWidth = (text: string, font: string): number => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) return 0
  context.font = font
  const metrics = context.measureText(text)
  return metrics.width
}

export const getTextSize = (text: string, fontSize: string, family?: string): Vector => {
  const el = EL<HTMLDivElement>('div', {
    innerText: text,
    style: `font-size: ${fontSize}; ${family ? 'font-family: ${family};' : ''}`
  })
  return getElementSize(el)
}

export const elementIntersectsWithPoint = (el: HTMLElement, point: Vector) => {
  const mpos = point
  const rect = el.getBoundingClientRect()
  const min = new Vector(rect.x, rect.y)
  const size = getElementSize(el)
  const max = min.add(VEC2(size.x, size.y))
  if (mpos.x < min.x || mpos.x > max.x) return false
  if (mpos.y < min.y || mpos.y > max.y) return false
  return true
}

export const renderVNode = (node: VNode): Element => {
  if (node.el) return node.el as HTMLElement
  const tmp = document.createElement('div')
  render(node, tmp)
  return (tmp.children.length === 1 && tmp.firstChild ? tmp.firstChild : tmp) as Element
}

export const renderElement = (el: TRenderableElement, props: ElProps = {}): string => {
  if (isString(el)) return el

  if (isHTMLElement(el)) {
    return el.outerHTML || el.innerHTML || el.innerText || ''
  }

  if (isVNode(el)) {
    if (el.el) {
      const htmlEl = el.el as HTMLElement
      return htmlEl.outerHTML
    }

    const tmp = document.createElement('div')
    render(el, tmp)
    return renderElement(tmp.children.length === 1 ? (tmp.firstChild as HTMLElement) : tmp, props)
  }

  if (isFunction(el)) {
    return renderElement((el as any)(props))
  }

  if (el.toString) return el.toString()

  return '<bold>invalid</bold>'
}

const toElement = (el: TRenderableElement, props: ElProps = {}): HTMLElement | HTMLElement[] => {
  if (isHTMLElement(el)) {
    if ((el as any).construct) {
      ;(el as any).construct(el)
    }
    return el
  }
  if (isFunction(el)) {
    return toElement((el as any)(props))
  }
  if (Array.isArray(el)) {
    return el.map((c) => renderElementToHTMLElement(c, getElProps(c))) as HTMLElement[]
  }
  const inner = renderElement(el, props)
  const wrapper = EL('div', {
    innerHTML: inner,
    css: { width: '100%', height: '100%' }
  })

  if (wrapper.children.length === 1 && wrapper.firstChild) {
    return wrapper.firstChild as HTMLElement
  }

  return wrapper
}

export const callElementFunction = <RT = any>(
  el: HTMLElement,
  func: string,
  ...args: any[]
): RT | undefined => {
  const e = el as unknown as any
  const f = e[func]
  if (!isFunction(f)) return undefined
  return f(...args) as RT
}

export const renderElementToHTMLElement = (
  el: TRenderableElement,
  props: ElProps = {}
): HTMLElement | HTMLElement[] => {
  if (Array.isArray(el)) el = toElement(el, props)
  if (isHTMLElement(el)) return el
  return toElement(el, props)
}

export const getVNodeElement = (node: VNode | Ref<VNode | undefined>): HTMLElement | undefined => {
  const g: VNode | undefined = isRef(node) ? node.value : node
  if (!g) return undefined
  if (isUndefined(g)) return undefined
  if ((g as any).$el) return (g as any).$el as HTMLElement
  if (isHTMLElement(g)) return g
  if (!g.el) return undefined
  return g.el as HTMLElement
}
